import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import UserPermissionTypes from '@constants/permissions';
import { SUBSCRIPTIONS } from '@constants/routes';
import { useAppDispatch } from '@core/store';
import {
  addDownload,
  updateDownloadStatus
} from '@features/downloads/downloadsSlice';
import { TRIAL_MOCK_IDS } from '@features/trial-mode/constants';
import useNotify from '@hooks/notify';
import { ApiError } from '@types';
import { downloadFileFromUrlUsingBlob } from '@utils/downloadFile';
import usePollDownloadStatus from '@utils/pollDownloadStatus';

type TrialReportGenerator =
  | 'generateTrialGstReport'
  | 'generateTrialCustomReport';

const pad = (value: number) => String(value).padStart(2, '0');

// Local-time YYYY-MM-DD_HHMMSS suffix for generated file names
const getFileTimestamp = (now = new Date()) =>
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(
    now.getHours()
  )}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

// Shared plumbing for report downloads: tray entries, job polling and error handling
const useReportJob = () => {
  const { createErrorAlert } = useNotify();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pollDownloadStatus, pollLeadReportJobStatus } =
    usePollDownloadStatus();
  const { rolesAndPermissions } = useSelector((state: any) => state.user);

  const redirectToPlanDetailsIfPermitted = () => {
    if (
      rolesAndPermissions.permissions.includes(
        UserPermissionTypes.SUBSCRIPTION_VIEW
      )
    ) {
      navigate(SUBSCRIPTIONS.PLAN_DETAILS);
    }
  };

  const registerDownload = (downloadId: string) =>
    dispatch(
      addDownload({ id: downloadId, fileName: downloadId, fileType: 'xlsx' })
    );

  // Adds the entry to the downloads tray, then polls the job until the file is ready
  const trackReportJob = async (
    params: Parameters<typeof pollDownloadStatus>[0]
  ) => {
    registerDownload(params.downloadId);
    await pollDownloadStatus(params);
  };

  // Returns the message so callers can include it in their analytics event
  const handleReportJobError = (error: ApiError, downloadId: string) => {
    const isPlanExpired = error?.status === 426;
    const message = isPlanExpired
      ? 'subscription plan has expired'
      : 'Error while downloading report';
    createErrorAlert(message);
    dispatch(
      updateDownloadStatus({ id: downloadId, status: 'error', error: message })
    );
    if (isPlanExpired) redirectToPlanDetailsIfPermitted();
    return message;
  };

  // Trial orgs have no backend data, so a mock workbook is generated locally
  const downloadTrialReport = async (
    generator: TrialReportGenerator,
    downloadId: string,
    filePrefix: string,
    { startDate, endDate }: { startDate: string; endDate: string }
  ) => {
    registerDownload(downloadId);
    try {
      const mockReports = await import(
        '@features/trial-mode/mock-data/reports'
      );
      const blob = await mockReports[generator](
        TRIAL_MOCK_IDS.ORG_NAME,
        startDate,
        endDate
      );
      const url = URL.createObjectURL(blob);
      await downloadFileFromUrlUsingBlob(
        url,
        `${filePrefix}_${getFileTimestamp()}.xlsx`
      );
      URL.revokeObjectURL(url);
      dispatch(updateDownloadStatus({ id: downloadId, status: 'completed' }));
    } catch {
      dispatch(
        updateDownloadStatus({
          id: downloadId,
          status: 'error',
          error: 'Failed to generate report'
        })
      );
    }
  };

  return {
    createErrorAlert,
    downloadTrialReport,
    handleReportJobError,
    pollLeadReportJobStatus,
    redirectToPlanDetailsIfPermitted,
    registerDownload,
    trackReportJob
  };
};

export default useReportJob;
